import { isAxiosError } from 'axios'
import axios from '@/_core/axiosClient'
import ERROR_TYPES from '@/constants/errorTypes'
import TOAST_MESSAGE_TYPES from '@/constants/toastMessageTypes'
// store は module top-level で生成せず、使う関数の中で useXxxStore() する。
// import 時（app.use(pinia) 前）に生成すると、stores/plugins/hydrateFromViewProps の対象外になり context が埋まらないため
import { useScreenLockStore } from '@/stores/screenLock'
import { useToastStore } from '@/stores/toast'
import { useErrorStore } from '@/stores/error'
import { useClientStore } from '@/stores/client'
import { hasOwnProperty } from '@/utilities/syntaxSugar'
import { datadogService } from '@/services/datadog'

const STATUS_TYPE = {
  INFORMATION       : 1,
  SUCCESS           : 2,
  REDIRECT_MESSAGE  : 3,
  CLIENT_ERROR      : 4,
  SERVER_ERROR      : 5,
}

// 処理対象のリクエストの情報
// handleRequest 時にテンポラリ情報として使用する
let requestProperty: ApiRequestProperty = initRequestProperty()

export default {
  /**
   * api リクエスト実行前の確認処理を実行する
   *
   * @return {Promise.<*>}
   */
  handleRequest(request: ApiRequestProperty) {
    Object.assign(requestProperty, request)

    return this.runRequest()
  },

  /**
   * ストリーミング API リクエストを実行する
   *
   * @param { ApiStreamRequestProperty } request streaming 用の API リクエストプロパティ
   */
  handleStreamRequest(request: ApiStreamRequestProperty) {
    Object.assign(requestProperty, request)

    const { endpoint, params } = requestProperty

    // ストリーミングAPIは現在POSTメソッドのみサポート
    if (endpoint.method.toLowerCase() !== 'post') {
      console.error('Streaming is only supported for POST requests')
      if (request.onError) request.onError(new Error('Streaming is only supported for POST requests'))
      return
    }

    const _config: ApiRequestConfig = {
      headers: {},
      responseType: 'stream',
    }

    // ストリーミングは終了経路が複数あるため、各経路で releaseLock を呼ぶ（何度呼んでも結果は同じ）。
    // 利用側のコールバック（onCompleted / onError）が例外を投げても解除が飛ばされないよう、必ずコールバックより先に呼ぶこと
    initRequestHeader(_config, endpoint)
    const releaseLock = acquireScreenLock(endpoint.screenLock)

    const url = endpoint.domain ? `${endpoint.domain}${endpoint.path}` : endpoint.path

    try {
      // Web Streams APIを使用したストリーミング処理
      fetch(url, {
        method: endpoint.method,
        headers: _config.headers as HeadersInit,
        body: JSON.stringify(params),
        credentials: endpoint.withCredentials ? 'include' : 'omit',
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          if (!response.body) {
            throw new Error('Response body is null')
          }

          // テキストデコーダーストリームを作成
          const textStream = response.body
            .pipeThrough(new TextDecoderStream())

            // ライン分割トランスフォーマーを作成
            .pipeThrough(new TransformStream({
              transform(chunk: string, controller: TransformStreamDefaultController<any>) {
                // チャンクをライン単位に分割
                const lines = chunk.split('\n').filter(line => line.trim() !== '')

                for (const line of lines) {
                  // 空行はスキップ
                  if (!line || line.trim() === '') continue

                  try {
                    // JSONパース
                    const jsonData = JSON.parse(line)

                    // エラープロパティをチェック
                    if (jsonData && jsonData.error) {
                      console.error('Server error in stream:', jsonData)
                      const streamError = new Error(jsonData.error || 'Server error')

                      // Track streaming error in Datadog
                      datadogService.trackError(streamError, {
                        error_type: 'streaming_api_error',
                        endpoint: endpoint.path,
                        method: endpoint.method,
                        error_data: jsonData,
                      })

                      controller.error(streamError)
                      return
                    }

                    // データ形式をチェック
                    if (jsonData && (typeof jsonData).toLowerCase() === 'object') {
                      controller.enqueue(jsonData)
                    } else {
                      console.warn('Unexpected data format in chunk:', jsonData)
                    }
                  } catch (e) {
                    console.error('Error parsing JSON chunk:', e, 'Raw data:', line)
                    // エラーでもストリーム処理は継続
                  }
                }
              }
            }))

          // ストリームからリーダーを作成
          const reader = textStream.getReader()

          // ストリーム処理関数
          async function processStream() {
            try {
              let isDone = false

              while (!isDone) {
                const { value, done } = await reader.read()

                if (done) {
                  // ストリーム終了時の処理
                  releaseLock()
                  if (request.onCompleted) request.onCompleted()
                  isDone = true
                  return
                }

                // 変換されたJSONデータをコールバックで処理
                if (value && request.onChunkReceived) {
                  request.onChunkReceived(value)
                }
              }
            } catch (error) {
              console.error('Stream processing error:', error)

              // Track stream processing error in Datadog
              datadogService.trackError(error as Error, {
                error_type: 'streaming_processing_error',
                endpoint: endpoint.path,
                method: endpoint.method,
              })

              releaseLock()
              if (request.onError) request.onError(error)
            }
          }

          // ストリーム処理を開始
          processStream()
        })
        .catch(error => {
          console.error('Fetch error:', error)

          // Track fetch error in Datadog
          datadogService.trackError(error as Error, {
            error_type: 'streaming_fetch_error',
            endpoint: endpoint.path,
            method: endpoint.method,
            url: url,
          })

          releaseLock()
          if (request.onError) request.onError(error)
        })
        .finally(() => {
          requestProperty = initRequestProperty()
        })
    } catch (e) {
      console.error('Unexpected error:', e)

      releaseLock()
      if (request.onError) request.onError(e)
    }
  },

  /**
   * api リクエストを実行する
   *
   * @return Promise<Object> api レスポンス
   */
  async runRequest() {
    const { endpoint, callback } = requestProperty

    let response
    switch (endpoint.method) {
      case 'get':
        response = await httpGet()
        break
      case 'post':
        response = await httpPost()
        break
      case 'put':
        response = await httpPut()
        break
      case 'delete':
        response = await httpDelete()
        break
    }

    if (callback) {
      const isSuccess = getStatusCategory(response) === STATUS_TYPE.SUCCESS
      callback(response, isSuccess)
    }

    requestProperty = initRequestProperty()

    return response
  },

  /**
   * api リクエストをキャンセルする
   */
  cancelRequest() {
    requestProperty = initRequestProperty()
  },
}

async function httpGet() {
  const _config: ApiRequestConfig = {
    headers: {},
  }

  const { params, endpoint } = requestProperty
  requestProperty = initRequestProperty()

  initRequestHeader(_config, endpoint)
  const releaseLock = acquireScreenLock(endpoint.screenLock)

  try {
    await handleClearErrors(endpoint.isClearErrorInfo)

    // Vue 側が Array に自動的に Observer というプロパティを付与してしまい、JSON parse error になるため、
    // リクエストの前処理として、params を JSON.stringify する
    Object.assign(_config, { params: JSON.parse(JSON.stringify(params)) } )

    const url = endpoint.domain ? `${endpoint.domain}${endpoint.path}` : endpoint.path
    const response = await axios.get(url, _config)
    processToast(response, endpoint)

    return response
  } catch (e) {
    return handleFailure(e, endpoint, params)
  } finally {
    releaseLock()
  }
}

async function httpPost() {
  const _config: ApiRequestConfig = {
    headers: {},
  }

  const { config, params, endpoint } = requestProperty
  requestProperty = initRequestProperty()

  Object.assign(_config, config)
  initRequestHeader(_config, endpoint)
  const releaseLock = acquireScreenLock(endpoint.screenLock)

  try {
    await handleClearErrors(endpoint.isClearErrorInfo)
    const url = endpoint.domain ? `${endpoint.domain}${endpoint.path}` : endpoint.path

    // Vue 側が Array に自動的に Observer というプロパティを付与してしまい、JSON parse error になるため、
    // リクエストの前処理として、params を JSON.stringify する
    const response = await axios.post(
      url,
      JSON.parse(JSON.stringify(params)),
      _config,
    )
    processToast(response, endpoint)

    return response
  } catch (e) {
    return handleFailure(e, endpoint, params)
  } finally {
    releaseLock()
  }
}

async function httpPut() {
  const _config: ApiRequestConfig = {
    headers: {},
  }

  const { config, params, endpoint } = requestProperty
  requestProperty = initRequestProperty()

  Object.assign(_config, config)
  initRequestHeader(_config, endpoint)
  const releaseLock = acquireScreenLock(endpoint.screenLock)

  try {
    await handleClearErrors(endpoint.isClearErrorInfo)
    const url = endpoint.domain ? `${endpoint.domain}${endpoint.path}` : endpoint.path

    // Vue 側が Array に自動的に Observer というプロパティを付与してしまい、JSON parse error になるため、
    // リクエストの前処理として、params を JSON.stringify する
    const response = await axios.put(
      url,
      JSON.parse(JSON.stringify(params)),
      _config,
    )
    processToast(response, endpoint)

    return response
  } catch (e) {
    return handleFailure(e, endpoint, params)
  } finally {
    releaseLock()
  }
}

async function httpDelete() {
  const _config: ApiRequestConfig = {
    headers: {},
  }

  const { params, endpoint, config } = requestProperty
  requestProperty = initRequestProperty()

  Object.assign(_config, config)
  initRequestHeader(_config, endpoint)
  const releaseLock = acquireScreenLock(endpoint.screenLock)

  try {
    await handleClearErrors(endpoint.isClearErrorInfo)

    // Vue 側が Array に自動的に Observer というプロパティを付与してしまい、JSON parse error になるため、
    // リクエストの前処理として、params を JSON.stringify する
    Object.assign(_config, { params: JSON.parse(JSON.stringify(params)) } )

    const url = endpoint.domain ? `${endpoint.domain}${endpoint.path}` : endpoint.path
    const response = await axios.delete(url, _config)

    return response
  } catch (e) {
    return handleFailure(e, endpoint, params)
  } finally {
    releaseLock()
  }
}

function initRequestHeader(config: ApiRequestConfig, endpoint: ApiRequestInfo) {
  if (endpoint.headers) {
    // endpoint 側の設定でリクエストヘッダーから省きたいプロパティがあれば（空値、もしくは false 値）、プロパティ自体を削除
    Object.keys(endpoint.headers).forEach(key => {
      if (!endpoint.headers![key]) {
        delete endpoint.headers![key]
      }
    })
  }

  // axios client に渡す config に endpoint の header 設定を assign
  Object.assign(config, { headers: endpoint.headers })

  // Set withCredentials if specified in the endpoint options
  if (endpoint.withCredentials !== undefined) {
    config.withCredentials = endpoint.withCredentials
  }

  // CSRF トークンをリクエストヘッダーに付与
  if (endpoint.hasCsrfCheck) {
    Object.assign(config.headers, { 'X-CSRF-TOKEN': useClientStore().context.csrfToken })
  }
}

/**
 * API リクエスト失敗時の分岐
 * @description 呼び出し側で失敗を判定するエンドポイント（hasDefaultErrorHandling: false）は、画面のエラー状態を書き換えずに例外をそのまま投げ返す。
 *              それ以外は共通のエラーハンドリングに流し、呼び出し側には false を返す。
 * @param e キャッチした例外オブジェクト
 * @param endpoint リクエストしたエンドポイントの設定
 * @param params リクエストパラメータ
 */
function handleFailure(e: unknown, endpoint: ApiRequestInfo, params: ApiRequestProperty['params']): false {
  if (endpoint.hasDefaultErrorHandling === false) throw e

  handleError(e, endpoint, params)
  return false
}

/**
 * API リクエストでエラーを catch した際の共通処理
 * @param e キャッチした例外オブジェクト
 * @param endpoint リクエスト先の情報
 * @param params リクエストパラメータ
 */
function handleError(e: any, endpoint: ApiRequestInfo, params: ApiRequestProperty['params']): void {
  // 予知できない例外エラーの場合のメッセージ
  const globalError: { cause: ErrorType, items: ErrorItemInput[] } = {
    cause: ERROR_TYPES.SERVER_ERROR,
    items: [
      {
        field: '_global',
        messages: [
          e.message ? e.message : 'エラーが発生しました。',
        ]
      }
    ]
  }

  const _error = e.response && hasOwnProperty(e.response.data, 'error')
    ? e.response.data.error
    : globalError

  // axios の失敗は axiosClient の response interceptor（error_type=api_error）が送る。
  // それ以外（レスポンス処理中の例外＝コードの不具合）は interceptor を通らないのでここで送る
  if (!isAxiosError(e)) {
    const allowedParams = Object.fromEntries(
      (endpoint.datadogAllowedParamKeys ?? [])
        .filter(key => hasOwnProperty(params, key))
        .map(key => [key, params?.[key]]),
    )

    datadogService.trackError(e instanceof Error ? e : new Error(String(e)), {
      error_type: 'api_request_manager_error',
      endpoint: endpoint.path,
      method: endpoint.method,
      ...(Object.keys(allowedParams).length > 0 ? { params: allowedParams } : {}),
    })
  }

  // store のエラーハンドリング処理に流す
  useErrorStore().handleErrors(_error)
}

function initRequestProperty(): ApiRequestProperty {
  return {
    endpoint: {
      path: '',
      method: 'get',
      screenLock: {
        hasLockScreen: true,
        hasBlackOut: false,
        hasBlur: false,
        hasWaitAnimation: false,
      },
    },
    params: {},
    callback: () => {},
    config: {},
  }
}

/**
 * 成功通知処理を実行する。各コンポーネント間での共用を想定し、ストアに格納する。
 * endpoint 単位でトーストの表示有無・表示メッセージの内容を設定している
 * @see _core/endpoint.ts
 */
function processToast(response: any, endpoint: ApiRequestInfo) {
  const id = `toast-${new Date().getTime()}`
  const processMessage = endpoint.processMessage || ''

  let messageSuffix = ''
  let messageType: MessageType = undefined
  if (getStatusCategory(response) === STATUS_TYPE.SUCCESS) {
    messageSuffix = 'しました。'
    messageType = TOAST_MESSAGE_TYPES.SUCCESS as MessageType
  } else {
    messageSuffix = 'に失敗しました。'
    messageType = TOAST_MESSAGE_TYPES.ALERT as MessageType
  }

  const message = `${processMessage}${messageSuffix}`

  if (endpoint.hasToastMessage && message) {
    useToastStore().setToastItem({ id, messageType, message })
  }
}

/**
 * レスポンスのステータスコードからステータスの区分け番号を返却
 * @param response
 * @returns 1: 情報レスポンス
 *          2: 成功
 *          3: リダイレクトメッセージ
 *          4: クライアントエラー
 *          5: サーバエラー
 * @see https://developer.mozilla.org/ja/docs/Web/HTTP/Status#%E6%83%85%E5%A0%B1%E3%83%AC%E3%82%B9%E3%83%9D%E3%83%B3%E3%82%B9
 */
function getStatusCategory(response: any): number | undefined {
  if (!response || !response.status) return

  return Math.floor(response.status / 100)
}

/**
 * endpoint の設定に従って画面ロックを取得する
 *
 * @param option endpoint の screenLock オプション
 * @return 取得したロックを手放す関数。何度呼んでも結果は同じ
 * @description
 *  ロックはリクエストごとに取得し、手放すのも自分の分だけ（モーダル等、他のロックには触れない）。
 *  hasLockScreen が false の場合も何もしない関数を返すのは、呼び出し側が分岐せず常に finally で呼べるようにするため。
 *  呼び出し側は try の直前で取得すること（取得から try までの間に例外が出ると finally を通らず、ロックが残る）
 * @see _core/endpoint.ts の requestOptions 内の screenLock オプション
 */
function acquireScreenLock(option: ApiRequestScreenLockOptions): () => void {
  if (!option.hasLockScreen) return () => {}

  return useScreenLockStore().acquireLock(option)
}

/**
 * 既に表示されているエラーメッセージの削除ハンドリング
 * @param clearErrorInfo エンドポイント毎のリクエスト時の既表示エラーメッセージの削除オプション
 * @description 現 academy は、現状グローバルエラーが存在していないので（トーストメッセージとしてアラートがでることはあるが、その場合はトーストなので、自動的にフェードアウトしていく）、実態としてはバリデーションエラーのみのハンドリングとなる。
 */
async function handleClearErrors(clearErrorInfo: clearErrorInfo | undefined) {
  if (clearErrorInfo && clearErrorInfo.validationError) {
    await useErrorStore().clearErrors()
  }
}
